import { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import StyledDataGrid from '../DataGrid/StyledDataGrid';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import { decimalFormatter } from '../../../../Helpers/DecimalFormatter';
import { WorkforceProfileDto } from '../../../Model/Dto/EmploymentEquity/WorkforceProfile_EEA2/WorkforceProfile';
import { EEOccupationalLevel } from '../../../Model/Enum/EmploymentEquity';
import { Gender, RaceTypes } from '../../../Model/Enum/BEE123Enums';

interface IProps {
    tableData: WorkforceProfileDto.SummarySectionDto[];
    onSegmentClick: (occupationalLevel?: EEOccupationalLevel, race?: RaceTypes, gender?: Gender, isPermanent?: boolean) => void;
}

export const HeadcountSummaryTable = (props: IProps) => {
    const [tableRows, setTableRows] = useState<any[]>([]);
    const raceGenderPairKeys = ["AM","AF", "CM","CF", "IM","IF", "WM", "WF", "FNM", "FNF"];
    
    useEffect(() => {
        createTableRows();
    }, [props.tableData]);

    const createTableRows = () => {
        let rows: any[] = [];
        props.tableData.forEach((section: WorkforceProfileDto.SummarySectionDto) => {
            rows.push({
                id: section.sectionLabel + 'spacer',
                description: section.sectionLabel,
                isSpacer: true
            });
            rows.push({
                id: `${section.sectionLabel} Headcount`,
                description:"Headcount (value)",
                ...mapRowData(section.headcountValuesRow),
                isHeadcount: true,
                occupationalLevel: section.eeOccupationalLevel,
                sectionLabel: section.sectionLabel,
            });
             rows.push({
                id: `${section.sectionLabel} Percentage`,
                description: "Percentage (%)",
                ...mapRowData(section.headcountPercentagesRow, section.headcountValuesRow.total == 0),
                isPercentage: true,
                occupationalLevel: section.eeOccupationalLevel,
                sectionLabel: section.sectionLabel,
            });
        });
        setTableRows(rows);
    }

    const getIsPermanentValue = (label: string) => {
        let isPermanent: boolean | undefined = true;
        if (label.includes("Temporary")){
            isPermanent = false;
        }
        else if (label.includes("Grand Total")){
            isPermanent = undefined;
        }
        return isPermanent;
    }
    const mapRowData = (rowData: WorkforceProfileDto.SummaryRowDto, showNA?: boolean) => {
          let mappedRow = {
                [raceGenderPairKeys[0]]: 
                {
                    race: RaceTypes.African, 
                    gender: Gender.Male, 
                    cellValue: showNA ? "N/A" : rowData.africanMales,
                    isNA: showNA
                },
                [raceGenderPairKeys[1]]: 
                {
                    race: RaceTypes.African, 
                    gender: Gender.Female, 
                    cellValue: showNA ? "N/A" : rowData.africanFemales,
                    isNA: showNA
                },
                [raceGenderPairKeys[2]]: 
                {
                    race: RaceTypes.Coloured, 
                    gender: Gender.Male, 
                    cellValue: showNA ? "N/A" : rowData.colouredMales,
                    isNA: showNA
                },
                [raceGenderPairKeys[3]]: 
                {
                    race: RaceTypes.Coloured, 
                    gender: Gender.Female, 
                    cellValue: showNA ? "N/A" : rowData.colouredFemales,
                    isNA: showNA
                },
                [raceGenderPairKeys[4]]: 
                {
                    race: RaceTypes.Indian, 
                    gender: Gender.Male, 
                    cellValue: showNA ? "N/A" : rowData.indianMales,
                    isNA: showNA
                },
                [raceGenderPairKeys[5]]: 
                {
                    race: RaceTypes.Indian, 
                    gender: Gender.Female, 
                    cellValue: showNA ? "N/A" : rowData.indianFemales,
                    isNA: showNA
                },
                [raceGenderPairKeys[6]]: 
                {
                    race: RaceTypes.White, 
                    gender: Gender.Male, 
                    cellValue: showNA ? "N/A" : rowData.whiteMales,
                    isNA: showNA
                },
                [raceGenderPairKeys[7]]: 
                {
                    race: RaceTypes.White, 
                    gender: Gender.Female, 
                    cellValue: showNA ? "N/A" : rowData.whiteFemales,
                    isNA: showNA
                },
                [raceGenderPairKeys[8]]: 
                {
                    race: RaceTypes.NonRSA, 
                    gender: Gender.Male, 
                    cellValue: showNA ? "N/A" : rowData.nonSAMales,
                    isNA: showNA
                },
                [raceGenderPairKeys[9]]: 
                {
                    race: RaceTypes.NonRSA, 
                    gender: Gender.Female, 
                    cellValue: showNA ? "N/A" : rowData.nonSAFemales,
                    isNA: showNA
                },
                "total": 
                {
                    cellValue: showNA ? "N/A" : rowData.total,
                    isNA: showNA
                },
                
            };
        return mappedRow;
    }

    const columns = (): GridColDef[] => [
        {
            field: "description",
            headerName: "Occupational Level",
            width: 200,
            disableColumnMenu: true,
            sortable: false,
            headerAlign: "left",
            headerClassName: "hideRightSeparator",
            cellClassName: "left-align-cell",
            colSpan: (params): number | undefined => {
                if (params.row.isSpacer ?? false) {
                    return 12;
                } else {
                    return 1;
                }
            },
            renderCell: (params) => {
                const description = params.value || "";
                return description;

            }
        },
        ...raceGenderPairKeys.flatMap((category: string, index: number) => {
            const isEvenIndex = index % 2 === 0;
            const cellClass = isEvenIndex ? "greyed-out-cell" : "";

            const fields: GridColDef[] = [
                {
                    field: `${category}`,
                    headerName: `${category}`,
                    minWidth: 60,
                    flex: 0.4,
                    disableColumnMenu: true,
                    sortable: false,
                    headerAlign: "center" as GridAlignment,
                    headerClassName: "hideRightSeparator",
                    type: 'number',
                    cellClassName: (params) => {
                        return params.row.isSpacer ? "" : (params.value.isNA ? cellClass : cellClass + " clickable-cell");
                    },
                    renderCell: (params) => {
                        if (params.row.isSpacer) {
                            let value = params.value.cellValue;
                            value = "";
                            return (value);
                        
                        } else if (params.value.cellValue !== undefined && (params.row.isPercentage || params.row.isHeadcount)) {
                            let isPermanent = getIsPermanentValue(params.row.sectionLabel);
                            if(params.value.isNA)
                            {
                                return params.row.isPercentage ? decimalFormatter(params.value.cellValue, 1) : params.value.cellValue
                            }
                            else
                            {
                                return (
                                    <span
                                        onClick={() => {
                                            props.onSegmentClick(params.row.occupationalLevel, params.value.race, params.value.gender, isPermanent);
                                        }}
                                    >
                                    {params.row.isPercentage ? decimalFormatter(params.value.cellValue, 1) : params.value.cellValue}
                                    </span>
                                )
                            }
                        }  
                        else {
                            return 0;
                        }
                    }
                }
            ];
            return fields;
        }),
        {
            field: "total",
            headerName: "Total",
            minWidth: 60,
            flex: 0.4,
            disableColumnMenu: true,
            sortable: false,
            headerAlign: "center",
            headerClassName: "hideRightSeparator",
            cellClassName: (params) => {
                let cellClass = 'greyed-out-cell'
                return params.row.isSpacer ? "" : (params.value.isNA ? cellClass : cellClass + " clickable-cell");
            },
            renderCell: (params) => {
                let isPermanent = getIsPermanentValue(params.row.sectionLabel);
                if(params.value.isNA)
                {
                    return params.value.cellValue
                }
                else
                {
                    return (
                        <span
                            onClick={() => {
                                props.onSegmentClick(params.row.occupationalLevel, undefined, undefined, isPermanent);
                            }}
                        >
                        {params.value.cellValue}
                        </span>
                    )
                } 
            }
        }
    ];

    return (
        <>
            <Grid container direction="row" style={{width: "100%", overflow: "hidden"}}>
                <Grid item xs={12}>
                    <StyledDataGrid
                        rows={tableRows}
                        columns={columns()}
                        disableSelectionOnClick
                        hideFooter={true}
                        getRowClassName={(params) => params.row.isSpacer ? "spacer-row" : "value-row"}
                        getRowHeight={() => 'auto'}
                        autoHeight={true}
                    />
                </Grid>
            </Grid>
        </>
    );
}